import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, Menu, X, Phone, Mail } from 'lucide-react';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';

// Default fallback menus per website type
const DEFAULT_MENUS = {
  business: [
    { id: 'home', label: 'Home', section: 'home', children: [] },
    { id: 'about', label: 'About Us', section: 'about', children: [] },
    { id: 'services', label: 'Services', section: 'services', children: [] },
    { id: 'testimonials', label: 'Testimonials', section: 'testimonials', children: [] },
    { id: 'contact', label: 'Contact', section: 'contact', children: [], highlight: true },
  ],
  portfolio: [
    { id: 'home', label: 'Home', section: 'home', children: [] },
    { id: 'profile', label: 'About', section: 'profile', children: [] },
    { id: 'services', label: 'Services', section: 'services', children: [] },
    { id: 'contact', label: 'Contact', section: 'contact', children: [], highlight: true },
  ],
};

const PublicNavbar = ({ onNavigate, activeSection, accentColor = '#6366f1', showTopBar = true }) => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [menuItems, setMenuItems] = useState([]);
  const [websiteType, setWebsiteType] = useState('portfolio');
  const [contactInfo, setContactInfo] = useState({ phone: '', email: '' });
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dropdownRef = useRef(null);

  // Handle window resize for responsive display
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        if (username) {
          // Fetch menu config
          console.log('PublicNavbar: Fetching menu for', username);
          const res = await api.get(`/business/menu/${username}/`);
          console.log('PublicNavbar: API response:', res.data);
          const wType = res.data.website_type || 'portfolio';
          setWebsiteType(wType);

          // Filter out hidden items (visible === false)
          let items = (res.data.menu_items || [])
            .filter(item => item.visible !== false)
            .map(item => ({
              ...item,
              // Also filter hidden children
              children: (item.children || []).filter(child => child.visible !== false)
            }));

          console.log('PublicNavbar: Processed menu items:', items);

          // If no menu items, use default for website type
          if (items.length === 0) {
            console.log('PublicNavbar: No items from API, using default for', wType);
            items = DEFAULT_MENUS[wType] || DEFAULT_MENUS.portfolio;
          }
          setMenuItems(items);

          // Fetch contact info for top bar
          try {
            const profileRes = await api.get(`/public/profile/${username}/`);
            setContactInfo({
              phone: profileRes.data?.userProfile?.phone || profileRes.data?.phone || '',
              email: profileRes.data?.userProfile?.email || profileRes.data?.email || '',
            });
            // Get logo if available
            if (profileRes.data?.userProfile?.profile_picture) {
              setLogoUrl(profileRes.data.userProfile.profile_picture);
            }
          } catch (e) {
            console.log('Could not fetch contact info');
          }
        }
      } catch (err) {
        console.error('Error fetching menu:', err);
        // Use default menu on error
        setMenuItems(DEFAULT_MENUS.portfolio);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, [username]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item) => {
    console.log('PublicNavbar: handleItemClick called', {
      id: item.id,
      section: item.section,
      children: item.children?.length,
      hasOnNavigate: !!onNavigate
    });

    if (item.children && item.children.length > 0 && !item.section && !item.url) {
      // Toggle dropdown only if item has no direct navigation target
      console.log('PublicNavbar: Toggling dropdown for', item.id);
      setOpenDropdown(openDropdown === item.id ? null : item.id);
    } else {
      // Navigate - use section, url, or id as fallback
      const target = item.section || item.url || item.id;
      console.log('PublicNavbar: Navigating to target:', target);
      if (target) {
        onNavigate?.(target);
        setOpenDropdown(null);
        setMobileMenuOpen(false);
      }
    }
  };

  const handleChildClick = (child) => {
    console.log('PublicNavbar: handleChildClick called', {
      id: child.id,
      section: child.section,
      hasOnNavigate: !!onNavigate
    });
    // Navigate - use section, url, or id as fallback
    const target = child.section || child.url || child.id;
    console.log('PublicNavbar: Child navigating to target:', target);
    if (target) {
      onNavigate?.(target);
      setOpenDropdown(null);
      setMobileMenuOpen(false);
    }
  };

  if (loading) {
    return null;
  }

  // Calculate top offset based on whether top bar is shown
  const hasTopBar = showTopBar && (contactInfo.phone || contactInfo.email);
  const navTop = hasTopBar ? '2.5rem' : '0';

  return (
    <>
      {/* Top Contact Bar */}
      {hasTopBar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '2.5rem',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 101,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '2rem',
          padding: '0 2rem',
        }}>
          {contactInfo.phone && (
            <a href={`tel:${contactInfo.phone}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}>
              <Phone size={14} />
              {contactInfo.phone}
            </a>
          )}
          {contactInfo.email && (
            <a href={`mailto:${contactInfo.email}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}>
              <Mail size={14} />
              {contactInfo.email}
            </a>
          )}
        </div>
      )}

      {/* Main Navigation */}
      <nav
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: navTop,
          left: 0,
          right: 0,
          zIndex: 100,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          padding: '0.75rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
      {/* Desktop Menu */}
      <div
        style={{
          display: isMobile ? 'none' : 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {menuItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = activeSection === item.section ||
            (hasChildren && item.children.some(c => c.section === activeSection));
          const isOpen = openDropdown === item.id;

          return (
            <div key={item.id} style={{ position: 'relative' }}>
              <button
                onClick={() => handleItemClick(item)}
                style={{
                  padding: item.highlight ? '0.5rem 1.25rem' : '0.5rem 1rem',
                  borderRadius: '999px',
                  border: 'none',
                  backgroundColor: item.highlight
                    ? accentColor
                    : isActive
                    ? 'rgba(255,255,255,0.1)'
                    : 'transparent',
                  cursor: 'pointer',
                  color: item.highlight ? '#fff' : isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem',
                  fontWeight: item.highlight ? '600' : '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  transition: 'all 0.2s ease',
                }}
              >
                {item.label}
                {hasChildren && (
                  <ChevronDown
                    size={14}
                    style={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  />
                )}
              </button>

              {/* Dropdown */}
              {hasChildren && isOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '0.5rem',
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '0.75rem',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '0.5rem',
                    minWidth: '180px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                  }}
                >
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleChildClick(child)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.625rem 1rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        backgroundColor: activeSection === child.section
                          ? 'rgba(255,255,255,0.1)'
                          : 'transparent',
                        cursor: 'pointer',
                        color: activeSection === child.section ? '#fff' : 'rgba(255,255,255,0.7)',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          activeSection === child.section ? 'rgba(255,255,255,0.1)' : 'transparent';
                      }}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        style={{
          display: isMobile ? 'flex' : 'none',
          padding: '0.5rem',
          background: 'none',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu */}
      {mobileMenuOpen && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: '4rem',
            left: '1rem',
            right: '1rem',
            backgroundColor: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(12px)',
            borderRadius: '1rem',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '1rem',
            zIndex: 101,
          }}
        >
          {menuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => handleItemClick(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor: activeSection === item.section ? 'rgba(255,255,255,0.1)' : 'transparent',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '1rem',
                }}
              >
                {item.label}
                {item.children?.length > 0 && (
                  <ChevronDown
                    size={16}
                    style={{
                      transform: openDropdown === item.id ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  />
                )}
              </button>
              {item.children?.length > 0 && openDropdown === item.id && (
                <div style={{ paddingLeft: '1rem' }}>
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleChildClick(child)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                      }}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </nav>
    </>
  );
};

export default PublicNavbar;
